import { useState, useEffect, useCallback } from 'react';
import type {
  TenantAdminRecipient,
  SendNotificationDto,
  SendBulkNotificationDto,
  BroadcastNotificationDto,
  NotificationSendResult,
} from '@yawi/types';
import { useServices, useToast } from '@/app/providers';

interface UseAdminNotificationsReturn {
  tenantAdmins: TenantAdminRecipient[];
  isLoading: boolean;
  isSending: boolean;
  loadTenantAdmins: (tenantId?: string) => Promise<void>;
  send: (data: SendNotificationDto) => Promise<NotificationSendResult>;
  sendBulk: (data: SendBulkNotificationDto) => Promise<NotificationSendResult>;
  sendToTenant: (tenantId: string, data: BroadcastNotificationDto) => Promise<NotificationSendResult>;
  broadcast: (data: BroadcastNotificationDto) => Promise<NotificationSendResult>;
}

export function useAdminNotifications(): UseAdminNotificationsReturn {
  const { adminNotificationRepository } = useServices();
  const { showToast } = useToast();
  const [tenantAdmins, setTenantAdmins] = useState<TenantAdminRecipient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const loadTenantAdmins = useCallback(async (tenantId?: string) => {
    setIsLoading(true);
    try {
      const data = await adminNotificationRepository.getTenantAdmins(tenantId);
      setTenantAdmins(data);
    } catch (err) {
      console.error('Failed to load tenant admins:', err);
      showToast('error', 'Error al cargar administradores');
      setTenantAdmins([]);
    } finally {
      setIsLoading(false);
    }
  }, [adminNotificationRepository, showToast]);

  useEffect(() => {
    loadTenantAdmins();
  }, [loadTenantAdmins]);

  const send = useCallback(async (data: SendNotificationDto): Promise<NotificationSendResult> => {
    setIsSending(true);
    try {
      const result = await adminNotificationRepository.send(data);
      showToast('success', result.message || 'Notificación enviada');
      return result;
    } catch (err) {
      console.error('Failed to send notification:', err);
      const message = err instanceof Error ? err.message : 'Error al enviar notificación';
      showToast('error', message);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [adminNotificationRepository, showToast]);

  const sendBulk = useCallback(async (data: SendBulkNotificationDto): Promise<NotificationSendResult> => {
    setIsSending(true);
    try {
      const result = await adminNotificationRepository.sendBulk(data);
      showToast('success', `Enviadas: ${result.sent || 0}, Fallidas: ${result.failed || 0}`);
      return result;
    } catch (err) {
      console.error('Failed to send bulk notification:', err);
      const message = err instanceof Error ? err.message : 'Error al enviar notificaciones';
      showToast('error', message);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [adminNotificationRepository, showToast]);

  const sendToTenant = useCallback(async (tenantId: string, data: BroadcastNotificationDto): Promise<NotificationSendResult> => {
    setIsSending(true);
    try {
      const result = await adminNotificationRepository.sendToTenant(tenantId, data);
      showToast('success', `Enviadas: ${result.sent || 0}, Fallidas: ${result.failed || 0}`);
      return result;
    } catch (err) {
      console.error('Failed to send to tenant:', err);
      const message = err instanceof Error ? err.message : 'Error al enviar notificaciones';
      showToast('error', message);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [adminNotificationRepository, showToast]);

  const broadcast = useCallback(async (data: BroadcastNotificationDto): Promise<NotificationSendResult> => {
    setIsSending(true);
    try {
      const result = await adminNotificationRepository.broadcast(data);
      showToast('success', `Broadcast enviado: ${result.sent || 0} enviadas, ${result.failed || 0} fallidas`);
      return result;
    } catch (err) {
      console.error('Failed to broadcast:', err);
      const message = err instanceof Error ? err.message : 'Error al enviar broadcast';
      showToast('error', message);
      throw err;
    } finally {
      setIsSending(false);
    }
  }, [adminNotificationRepository, showToast]);

  return {
    tenantAdmins,
    isLoading,
    isSending,
    loadTenantAdmins,
    send,
    sendBulk,
    sendToTenant,
    broadcast,
  };
}
